import { useState, useMemo, useEffect } from "react";
import { Loader2, Printer, RefreshCw, Package, Search } from "lucide-react";
import { toast } from "sonner";
import { useActiveStore } from "@/hooks/use-active-store";
import { useLabelOrders, useAllLabelOrders, useGenerateLabels } from "@/hooks/use-fulfillment";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge, ORDER_STATUSES } from "@/components/orders/status-badge";
import { TablePagination } from "@/components/ui/table-pagination";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LabelPrintedTag, MetaOrderTag } from "@/components/meta-orders/meta-badge";
import type { LabelOrder as ServiceLabelOrder } from "@/services/fulfillment";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

/** Red liste; meta narudžba je jedan red (`orderId` = "M-12") s poljem `meta`. */
type LabelOrder = ServiceLabelOrder & { status?: string; dateCreated?: string };

/**
 * Štampanje s potvrdom: meta narudžba se vidi iz svakog uključenog shopa, pa već štampanu
 * naljepnicu prvo treba potvrditi. Meta redovi idu u `metaIds`, obične narudžbe u `orderIds`.
 */
function usePrintLabels(storeId: string | null) {
  const generateLabels = useGenerateLabels();
  const [pending, setPending] = useState<LabelOrder[] | null>(null);

  async function run(rows: LabelOrder[]) {
    if (!storeId) return;
    if (rows.length === 0) {
      toast.error("Nema narudžbi za printanje");
      return;
    }
    try {
      await generateLabels.mutateAsync({
        storeId,
        orderIds: rows.filter((o) => !o.meta).map((o) => o.orderId),
        metaIds: rows.flatMap((o) => (o.meta ? [o.meta.id] : [])),
      });
      toast.success("PDF generisan");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri generisanju naljepnica");
    }
  }

  function print(rows: LabelOrder[]) {
    if (rows.some((o) => o.meta?.labelPrinted)) setPending(rows);
    else void run(rows);
  }

  const printed = pending?.filter((o) => o.meta?.labelPrinted) ?? [];
  const others = pending?.filter((o) => !o.meta?.labelPrinted) ?? [];
  const numbers = printed.map((o) => o.meta!.number).join(", ");

  const confirmDialog = (
    <AlertDialog open={pending !== null} onOpenChange={(v) => { if (!v) setPending(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {printed.length === 1 ? "Naljepnica je već štampana" : "Naljepnice su već štampane"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {printed.length === 1
              ? `Naljepnica za ${numbers} je već štampana. Štampati ponovo?`
              : `Naljepnice za ${numbers} su već štampane. Štampati ponovo?`}
            {others.length > 0 && ` Ostalih naljepnica u ovom printu: ${others.length}.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Otkaži</AlertDialogCancel>
          {others.length > 0 && (
            <Button
              variant="outline"
              onClick={() => {
                setPending(null);
                void run(others);
              }}
            >
              Preskoči štampane
            </Button>
          )}
          <Button
            onClick={() => {
              const rows = pending ?? [];
              setPending(null);
              void run(rows);
            }}
          >
            Štampaj ponovo
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { print, isPending: generateLabels.isPending, confirmDialog };
}

function formatDate(iso: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("bs-BA", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function OrdersTable({
  orders,
  selected,
  toggleAll,
  toggleOne,
  allSelected,
  showStatus = false,
  showDate = false,
}: {
  orders: LabelOrder[];
  selected: Set<string>;
  toggleAll: () => void;
  toggleOne: (id: string) => void;
  allSelected: boolean;
  showStatus?: boolean;
  showDate?: boolean;
}) {
  return (
    <div className="ring-1 ring-foreground/10 overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="w-10 p-2 text-left">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
            </th>
            <th className="p-2 text-left font-medium">Narudžba</th>
            <th className="p-2 text-left font-medium">Kupac</th>
            {showStatus && <th className="p-2 text-left font-medium hidden md:table-cell">Status</th>}
            {showDate && <th className="p-2 text-left font-medium hidden md:table-cell">Datum</th>}
            <th className="p-2 text-right font-medium">Ukupno</th>
            <th className="p-2 text-left font-medium hidden sm:table-cell">Tracking</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.orderId} className="border-b hover:bg-muted/50 transition-colors">
              <td className="p-2">
                <Checkbox checked={selected.has(o.orderId)} onCheckedChange={() => toggleOne(o.orderId)} />
              </td>
              <td className="p-2 font-medium">
                {o.meta ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <MetaOrderTag number={o.meta.number} storeNames={o.meta.storeNames} />
                    {o.meta.labelPrinted && <LabelPrintedTag />}
                  </div>
                ) : (
                  `#${o.orderNumber}`
                )}
              </td>
              <td className="p-2 text-muted-foreground truncate max-w-[200px]">{o.customerName}</td>
              {showStatus && (
                <td className="p-2 hidden md:table-cell">
                  {o.status ? <StatusBadge status={o.status} /> : null}
                </td>
              )}
              {showDate && (
                <td className="p-2 text-muted-foreground hidden md:table-cell tabular-nums">
                  {formatDate(o.dateCreated || "")}
                </td>
              )}
              <td className="p-2 text-right tabular-nums">{parseFloat(o.total || "0").toFixed(2)} KM</td>
              <td className="p-2 text-muted-foreground tabular-nums hidden sm:table-cell">{o.trackingNumber}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OptimizacijaTab({ storeId }: { storeId: string | null }) {
  const { data: orders, isLoading, refetch, isRefetching } = useLabelOrders(storeId);
  const printLabels = usePrintLabels(storeId);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const allIds = orders?.map((o) => o.orderId) ?? [];
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allIds));
  }

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleGenerate() {
    const all = orders ?? [];
    printLabels.print(selected.size > 0 ? all.filter((o) => selected.has(o.orderId)) : all);
  }

  const printCount = selected.size > 0 ? selected.size : (orders?.length ?? 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Narudžbe u obradi sa tracking brojem</p>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="gap-1.5"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? "animate-spin" : ""}`} />
            Osvježi
          </Button>
          {(orders?.length ?? 0) > 0 && (
            <Button
              size="sm"
              onClick={handleGenerate}
              disabled={printLabels.isPending}
              className="gap-1.5"
            >
              {printLabels.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Printer className="h-3.5 w-3.5" />
              )}
              Printaj ({printCount})
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : !orders?.length ? (
        <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
          <Package className="h-6 w-6" />
          <p className="text-xs">Nema narudžbi u obradi sa tracking brojem</p>
        </div>
      ) : (
        <OrdersTable
          orders={orders}
          selected={selected}
          toggleAll={toggleAll}
          toggleOne={toggleOne}
          allSelected={allSelected}
        />
      )}
      {printLabels.confirmDialog}
    </div>
  );
}

function ListaTab({ storeId }: { storeId: string | null }) {
  const { data: orders, isLoading, refetch, isRefetching } = useAllLabelOrders(storeId);
  const printLabels = usePrintLabels(storeId);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateAfter, setDateAfter] = useState("");
  const [dateBefore, setDateBefore] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const filtered = useMemo(() => {
    if (!orders) return [];
    const q = search.trim().toLowerCase();
    const after = dateAfter ? new Date(dateAfter).getTime() : null;
    const before = dateBefore ? new Date(dateBefore).getTime() + 24 * 60 * 60 * 1000 - 1 : null;
    return orders.filter((o) => {
      if (status && o.status !== status) return false;
      if (q) {
        const hay = `${o.orderNumber} ${o.customerName} ${o.trackingNumber} ${o.meta?.storeNames.join(" ") ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (after || before) {
        const t = o.dateCreated ? new Date(o.dateCreated).getTime() : NaN;
        if (isNaN(t)) return false;
        if (after && t < after) return false;
        if (before && t > before) return false;
      }
      return true;
    });
  }, [orders, search, status, dateAfter, dateBefore]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  useEffect(() => {
    setPage(1);
  }, [search, status, dateAfter, dateBefore, pageSize]);

  const paginated = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize],
  );

  const allIds = paginated.map((o) => o.orderId);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  function toggleAll() {
    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of allIds) next.delete(id);
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of allIds) next.add(id);
        return next;
      });
    }
  }

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function resetFilters() {
    setSearch("");
    setStatus("");
    setDateAfter("");
    setDateBefore("");
  }

  function handleGenerate() {
    printLabels.print(selected.size > 0 ? filtered.filter((o) => selected.has(o.orderId)) : filtered);
  }

  const selectedInFiltered = filtered.filter((o) => selected.has(o.orderId)).length;
  const printCount = selectedInFiltered > 0 ? selectedInFiltered : filtered.length;
  const hasFilters = !!(search || status || dateAfter || dateBefore);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {orders ? `${filtered.length} od ${orders.length} narudžbi` : "Sve uvezene narudžbe sa tracking brojem"}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="gap-1.5"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? "animate-spin" : ""}`} />
            Osvježi
          </Button>
          {filtered.length > 0 && (
            <Button
              size="sm"
              onClick={handleGenerate}
              disabled={printLabels.isPending}
              className="gap-1.5"
            >
              {printLabels.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Printer className="h-3.5 w-3.5" />
              )}
              Printaj ({printCount})
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pretraga (broj, kupac, tracking)"
            className="h-8 pl-7 text-xs"
          />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="h-8 w-36 text-xs">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {ORDER_STATUSES.map((s) => (
              <SelectItem key={s.value || "all"} value={s.value || "all"}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DatePicker
          value={dateAfter}
          onChange={setDateAfter}
          placeholder="Od datuma"
          className="h-8 w-36 text-xs"
        />
        <DatePicker
          value={dateBefore}
          onChange={setDateBefore}
          placeholder="Do datuma"
          className="h-8 w-36 text-xs"
        />
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={resetFilters} className="h-8 text-xs">
            Resetuj
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : !filtered.length ? (
        <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
          <Package className="h-6 w-6" />
          <p className="text-xs">
            {orders?.length ? "Nema rezultata za zadate filtere" : "Nema uvezenih narudžbi sa tracking brojem"}
          </p>
        </div>
      ) : (
        <>
          <OrdersTable
            orders={paginated}
            selected={selected}
            toggleAll={toggleAll}
            toggleOne={toggleOne}
            allSelected={allSelected}
            showStatus
            showDate
          />
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Po stranici</span>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger className="h-8 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <TablePagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
            />
            <div className="w-20" />
          </div>
        </>
      )}
      {printLabels.confirmDialog}
    </div>
  );
}

export function LabelsPage() {
  const { storeId } = useActiveStore();

  return (
    <div className="p-4 flex flex-col gap-3">
      <h2 className="text-sm font-medium">Naljepnice</h2>
      <Tabs defaultValue="optimizacija">
        <TabsList variant="line">
          <TabsTrigger value="optimizacija">Optimizacija</TabsTrigger>
          <TabsTrigger value="lista">Lista</TabsTrigger>
        </TabsList>

        <TabsContent value="optimizacija" className="flex flex-col gap-3">
          <OptimizacijaTab storeId={storeId} />
        </TabsContent>

        <TabsContent value="lista" className="flex flex-col gap-3">
          <ListaTab storeId={storeId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
